import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import styles from "./styles.module.scss";
import coachnow_logo from "app/images/Logo.svg";
import page_bg from "app/images/qr_page_bg.jpg";
import { useTranslation } from "react-i18next";
import appstore from "app/images/appstore-btn.png";
import playstore from "app/images/android-app-store-badge.svg";
import { cls } from "app/utils";
import { fetchGet } from "app/utils/request";

interface QrWorkflow {
  facilityName: string;
  activationToken: string;
}

const QrPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [workflow, setWorkflow] = useState<QrWorkflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const locale = params.get("locale");
    if (locale && i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [location.search, i18n]);

  useEffect(() => {
    fetchGet<QrWorkflow>("/qr/" + token, {}, { version: 1 })
      .then((data) => {
        setWorkflow(data as QrWorkflow);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderStep = (step: number) => {
    return <div className={styles.number_container}>{step}</div>;
  };

  const hyperLinkUrl = {
    playStoreLink:
      "https://play.google.com/store/apps/details?id=com.edufii&referrer=utm_source%3Dwebapp%26utm_medium%3Donboarding%26utm_campaign%3Dv1",
    appStoreLink:
      "https://apps.apple.com/app/apple-store/id596598472?pt=117236&ct=web-onboarding&mt=8",
  };

  if (loading) {
    return (
      <div className={cls("qr-page-wrapper", styles.container)}>
        <img src={page_bg} alt="page background" className={styles.page_bg} />
        <img
          src={coachnow_logo}
          alt="coachnow logo"
          className={styles.coachnow_logo}
        />
        <div className={styles.content_header_container}>
          <p>{t("Loading...")}</p>
        </div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className={cls("qr-page-wrapper", styles.container)}>
        <img src={page_bg} alt="page background" className={styles.page_bg} />
        <img
          src={coachnow_logo}
          alt="coachnow logo"
          className={styles.coachnow_logo}
        />
        <div className={styles.content_header_container}>
          <h2>{t("Invalid Link")}</h2>
          <p>{t("This QR code link is invalid or has expired.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls("qr-page-wrapper", styles.container)}>
      <img src={page_bg} alt="page background" className={styles.page_bg} />
      <img
        src={coachnow_logo}
        alt="coachnow logo"
        className={styles.coachnow_logo}
      />
      <div className={styles.content_header_container}>
        <h2>{t("Welcome to {{facilityName}}!", { facilityName: workflow.facilityName })}</h2>
        <p>
          {t(
            "Connect to {{facilityName}} on CoachNow to book sessions, make payments, access your golf videos and training journal.",
            { facilityName: workflow.facilityName },
          )}
        </p>
      </div>
      <div className={styles.steps_layout}>
        <div className={styles.steps_wrapper}>
          <div className={styles.step_container}>
            {renderStep(1)}
            <div className={styles.step_info_wrapper}>
              <p className={styles.step_num}>{t("STEP 1:")}</p>
              <p className={styles.step_info}>{t("Install CoachNow app")}</p>
              <div className={styles.stores_wrapper}>
                <a
                  href={hyperLinkUrl.appStoreLink}
                  rel="noopener noreferrer"
                >
                  <img
                    src={appstore}
                    alt="app store"
                    className={styles.store_img}
                  />
                </a>

                <a
                  href={hyperLinkUrl.playStoreLink}
                  rel="noopener noreferrer"
                >
                  <img
                    src={playstore}
                    alt="play store"
                    className={styles.store_img}
                  />
                </a>
              </div>
            </div>
          </div>

          <div className={styles.step_container}>
            {renderStep(2)}
            <div className={styles.step_info_wrapper}>
              <p className={styles.step_num}>{t("STEP 2:")}</p>
              <p className={styles.step_info}>
                {t(
                  "Once installed tap \u201CI was Invited\u201D and enter the code below",
                )}
              </p>
              <div
                className={styles.generated_code_container}
                onClick={() => handleCopy(workflow.activationToken)}
              >
                {copied && <p className={styles.copied}>Copied!</p>}
                {!copied && (
                  <>
                    <p className={styles.generated_code}>
                      {workflow.activationToken}
                    </p>
                    <i className={cls("ico ico-templates", styles.copy_ico)} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrPage;
